import React from 'react';
// eslint-disable-next-line import/default
import SelectDropdown from 'react-native-select-dropdown';
import { View } from 'react-native';

import { BranchAddress } from '@stores/lookups/types';
import { Icon } from '@components/Icon';
import { LocalisedServerValue } from '@components/LocalisedServerValue';
import { Typography } from '@components/Typography';
import { useTranslation } from '@providers/I18n';
import { makeStyles, useTheme } from '@providers/Theme';

const useStyles = makeStyles((theme) => ({
  dropdownCotainer: {
    alignItems: 'flex-start',
  },
  dropdownSelectInput: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 18,
  },
  selectInputText: {
    color: theme.palette.grey[600],
    marginHorizontal: 12,
  },
  selectInputTextItalic: {
    fontStyle: 'italic',
  },
  dropdownSearch: {
    padding: theme.spacing(0.5),
    boxShadow: 'none',
    border: 0,
  },
  dropdownRowChild: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingHorizontal: theme.spacing(1),
  },
  dropdownRowChildIcon: {
    flex: 1,
  },
  dropdownRowChildTxt: {
    flex: 10,
    color: theme.palette.grey[700],
  },
  dropdownSelectedRow: { backgroundColor: theme.palette.grey[200] },
  dropdownRow: {
    borderBottomColor: theme.palette.grey[200],
    paddingVertical: theme.spacing(2),
    height: 100,
    backgroundColor: 'white',
  },
  dropdown: {},
  dropdownSelectBtn: {
    minHeight: 50,
    height: 'auto',
    backgroundColor: '#FFF',
    paddingHorizontal: 0,
    borderWidth: 1,
    borderRadius: 8,
    borderColor: '#444',
    width: '100%',
    on: {
      xl: { width: '40%' },
      lg: { width: '60%' },
      md: { width: '80%' },
    },
  },
  dropdownIcon: {
    marginRight: 10,
    marginTop: 5,
  },
  searchInputTxt: {
    textAlign: theme.direction === 'rtl' ? 'right' : 'left',
    paddingLeft: theme.spacing(1),
  },
}));

type BranchesDropdownProps = {
  branches: BranchAddress[];
  selectedBranch: BranchAddress | null;
  loading: boolean;
  filterBranches: (searchValue: string) => void;
  fetchMenus: (branch: BranchAddress) => void;
};

export const BranchesDropdown: React.FC<BranchesDropdownProps> = ({
  branches,
  selectedBranch,
  loading,
  filterBranches,
  fetchMenus,
}) => {
  const { t, i18n } = useTranslation();
  const styles = useStyles();

  const theme = useTheme();
  const lang = i18n.language;

  return (
    <View style={styles.dropdownCotainer}>
      <SelectDropdown
        data={branches}
        disabled={loading}
        defaultValue={selectedBranch}
        onChangeSearchInputText={filterBranches}
        onSelect={fetchMenus}
        buttonStyle={styles.dropdownSelectBtn}
        renderDropdownIcon={() => (
          <Icon
            name="arrow-down"
            size={22}
            color={theme.palette.grey[600]}
            style={styles.dropdownIcon}
          />
        )}
        dropdownIconPosition={lang === 'ar' ? 'left' : 'right'}
        renderCustomizedButtonChild={(selectedItem, i) => {
          const item = selectedItem || selectedBranch;
          return (
            <View style={styles.dropdownSelectInput} key={i}>
              <Icon name="pin" size={16} color={theme.palette.grey[600]} />
              <Typography
                size="md"
                weight="regular"
                numberOfLines={3}
                style={styles.selectInputText}>
                {item ? (
                  <>
                    <Typography
                      size="md"
                      weight="semiBold"
                      style={styles.selectInputTextItalic}>
                      <LocalisedServerValue value={item?.restaurantName} />,{' '}
                    </Typography>
                    <LocalisedServerValue value={item?.branchAddress} />
                  </>
                ) : (
                  t('menu.selectBranch', 'Select Branch')
                )}
              </Typography>
            </View>
          );
        }}
        dropdownStyle={styles.dropdown}
        rowStyle={styles.dropdownRow}
        selectedRowStyle={styles.dropdownSelectedRow}
        searchInputTxtStyle={styles.searchInputTxt}
        renderCustomizedRowChild={(item) => {
          return (
            <View style={styles.dropdownRowChild} key={item.branchUUID}>
              <Icon
                name="pin"
                size={20}
                color={
                  selectedBranch &&
                  selectedBranch.branchUUID === item.branchUUID
                    ? theme.palette.primary[600]
                    : theme.palette.grey[600]
                }
                style={styles.dropdownRowChildIcon}
              />
              <View style={styles.dropdownRowChildTxt}>
                <Typography size="sm" weight="semiBold">
                  <LocalisedServerValue value={item?.restaurantName} />
                </Typography>
                <Typography size="md" weight="regular" numberOfLines={1}>
                  <LocalisedServerValue value={item?.branchAddress} />
                </Typography>
                <Typography size="md" weight="semiBold">
                  <LocalisedServerValue value={item?.zone} />,{' '}
                  <LocalisedServerValue value={item?.area} />
                </Typography>
              </View>
            </View>
          );
        }}
        search
        searchInputStyle={styles.dropdownSearch}
        searchPlaceHolder={t('menu.searchHere', 'Search here')}
        searchPlaceHolderColor={theme.palette.grey[600]}
      />
    </View>
  );
};
